import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Icon from '../ui/Icon';
import alertsApi, { ALERTS_CHANGED_EVENT } from '../alertsApi';
import { isShellRoute, openAppTitle } from './navConfig';
import './shell.css';

const beeLogo = `${process.env.PUBLIC_URL}/logo.png`;

export default function HiveTopBar({ userInfo, logout, isMobile, onToggleSidebar }) {
  const history = useHistory();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const appOpen = !isShellRoute(location.pathname);
  const initial = (userInfo?.firstname || userInfo?.username || 'B').trim().charAt(0).toUpperCase();

  // Contextual back returns to wherever you came from (e.g. Document Center →
  // Manage Folder → back = Document Center), falling back to Home.
  const goBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) history.goBack();
    else history.push('/');
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  // Contextual bar while an app is open: back + app title.
  if (appOpen) {
    return (
      <header className="hive-topbar hive-topbar-app">
        <div className="hive-topbar-inner hive-container">
          {!isMobile && onToggleSidebar && (
            <button
              type="button"
              className="hive-iconbtn hive-tap hive-focusable"
              onClick={onToggleSidebar}
              aria-label="Toggle menu"
            >
              <Icon name="Menu" size={22} />
            </button>
          )}
          <button
            type="button"
            className="hive-iconbtn hive-tap hive-focusable"
            onClick={goBack}
            aria-label="Back"
          >
            <Icon name="ArrowLeft" size={22} />
          </button>
          <span className="hive-topbar-apptitle">{openAppTitle(location.pathname)}</span>
          <div className="hive-topbar-actions">
            <ProfileButton
              initial={initial}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              menuRef={menuRef}
              userInfo={userInfo}
              logout={logout}
              history={history}
            />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="hive-topbar">
      <div className="hive-topbar-inner hive-container">
        {!isMobile && onToggleSidebar && (
          <button
            type="button"
            className="hive-iconbtn hive-tap hive-focusable"
            onClick={onToggleSidebar}
            aria-label="Toggle menu"
          >
            <Icon name="Menu" size={22} />
          </button>
        )}
        <button className="hive-brand hive-tap" onClick={() => history.push('/')} type="button">
          <img src={beeLogo} alt="" className="hive-brand-logo" />
          <span className="hive-brand-stack">
            <span className="hive-brand-line1">BeeNexa</span>
            <span className="hive-brand-line2 hive-gold-text">Hive</span>
          </span>
        </button>

        <div className="hive-topbar-actions">
          <button
            type="button"
            className="hive-iconbtn hive-tap hive-focusable"
            onClick={() => history.push('/store')}
            aria-label="Search apps"
          >
            <Icon name="Search" size={22} />
          </button>
          <BellButton onClick={() => history.push('/updates')} />
          <ProfileButton
            initial={initial}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            menuRef={menuRef}
            userInfo={userInfo}
            logout={logout}
            history={history}
          />
        </div>
      </div>
    </header>
  );
}

// Notifications bell with an unread-count badge. Polls the same alert feed the
// Updates screen shows, so a new access request / grant / invite bumps the count
// without a page reload (no websocket push is enabled).
function BellButton({ onClick }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => {
      alertsApi
        .list()
        .then((res) => {
          if (alive) setCount(Array.isArray(res?.data) ? res.data.length : 0);
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    // Refresh instantly when the Updates feed changes the alerts (dismiss /
    // clear all / answer an invite), so the badge doesn't lag behind.
    window.addEventListener(ALERTS_CHANGED_EVENT, load);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener(ALERTS_CHANGED_EVENT, load);
    };
  }, []);

  return (
    <button
      type="button"
      className="hive-iconbtn hive-tap hive-focusable"
      onClick={onClick}
      aria-label={count > 0 ? `Notifications (${count} new)` : 'Notifications'}
      style={{ position: 'relative' }}
    >
      <Icon name="Bell" size={22} />
      {count > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 2,
            right: 2,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 8,
            background: '#d9534f',
            color: '#fff',
            fontSize: 10,
            lineHeight: '16px',
            fontWeight: 700,
            textAlign: 'center',
            boxSizing: 'border-box',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

function ProfileButton({ initial, menuOpen, setMenuOpen, menuRef, userInfo, logout, history }) {
  return (
    <div className="hive-profile-wrap" ref={menuRef}>
      <button
        type="button"
        className="hive-avatar hive-tap hive-focusable"
        onClick={() => setMenuOpen((o) => !o)}
        aria-label="Account"
      >
        {initial}
      </button>
      {menuOpen && (
        <div className="hive-profile-menu">
          <div className="hive-profile-head">
            <div className="hive-profile-name">
              {[userInfo?.firstname, userInfo?.lastname].filter(Boolean).join(' ') || userInfo?.username}
            </div>
            <div className="hive-profile-email">{userInfo?.username}</div>
          </div>
          <button
            type="button"
            className="hive-profile-item"
            onClick={() => {
              setMenuOpen(false);
              history.push('/my-apps');
            }}
          >
            <Icon name="Layers" size={16} /> My Apps
          </button>
          <button
            type="button"
            className="hive-profile-item"
            onClick={() => {
              setMenuOpen(false);
              history.push('/more');
            }}
          >
            <Icon name="Settings" size={16} /> Settings & more
          </button>
          <button type="button" className="hive-profile-item hive-profile-danger" onClick={logout}>
            <Icon name="LogOut" size={16} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
